import type { Connect4View } from '@minigame-race/contracts';
import { color } from '../theme.js';
import { Icon } from '../components/Icon.js';
import { TurnMoveTimer } from './TurnMoveTimer.js';

// You are always lime, your rival always cyan — independent of which SEAT you
// hold. `colorFor` maps an absolute seat to that you-relative colour.
const YOU_COLOR = '#a6ff2e';
const RIVAL_COLOR = '#22e0ff';
const colorFor = (seat: number, yourSeat: number): string => (seat === yourSeat ? YOU_COLOR : RIVAL_COLOR);

export function ConnectFourBoard({
  view,
  yourSeat,
  yourTurn,
  onMove,
}: {
  view: Connect4View;
  yourSeat: number;
  yourTurn: boolean;
  onMove: (move: unknown) => void;
}) {
  const { cols, rows, cells, winLine, moveMsLeft, moveCapMs } = view;
  const win = new Set(winLine ?? []);

  const colFull = (c: number): boolean => cells[c] !== -1; // top row of column occupied

  // Fit BOTH dimensions: the board is `cols`-wide × `rows`-tall. Cap the width by
  // the container's width AND by a height budget (so 6 rows + the timer, drop
  // buttons and legend never overflow → no scroll). The board's own height is
  // width × rows/cols, so budget the height back into a width ceiling.
  const boardW = `min(96cqw, ${Math.round((72 / rows) * cols)}cqh)`;

  return (
    <div style={{ width: boardW, maxWidth: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'min(1.6cqh,10px)' }}>
      <TurnMoveTimer yourTurn={yourTurn} moveMsLeft={moveMsLeft} moveCapMs={moveCapMs} boardSig={cells.join(',')} />

      {/* drop buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols},1fr)`, gap: 'min(1.4cqw,8px)' }}>
        {Array.from({ length: cols }, (_, c) => {
          const active = yourTurn && !colFull(c);
          return (
            <button
              key={c}
              type="button"
              disabled={!active}
              onClick={() => onMove({ type: 'drop', col: c })}
              aria-label={`Drop in column ${c + 1}`}
              style={{
                height: 'min(6cqh,36px)',
                borderRadius: 8,
                border: `1px solid ${active ? 'rgba(166,255,46,.5)' : 'rgba(234,255,234,.12)'}`,
                background: active ? 'rgba(166,255,46,.14)' : 'rgba(255,255,255,.03)',
                color: active ? color.lime : 'rgba(234,255,234,.3)',
                cursor: active ? 'pointer' : 'default',
                fontSize: 'min(4cqw,20px)',
                fontWeight: 700,
                display: 'grid',
                placeItems: 'center',
                touchAction: 'manipulation',
              }}
            >
              <Icon name="arrow-down" size="min(4.5cqw,20px)" stroke={2.4} />
            </button>
          );
        })}
      </div>

      {/* board */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols},1fr)`,
          gap: 'min(1.4cqw,8px)',
          padding: 'min(1.6cqw,10px)',
          borderRadius: 14,
          background: 'rgba(0,40,80,.3)',
          border: '1px solid rgba(166,255,46,.16)',
        }}
      >
        {cells.map((v, i) => {
          const c = v === -1 ? null : colorFor(v, yourSeat);
          return (
            <div
              key={i}
              style={{
                aspectRatio: '1 / 1',
                width: '100%',
                borderRadius: '50%',
                background: c ?? 'rgba(0,0,0,.35)',
                boxShadow: win.has(i)
                  ? '0 0 0 3px #fff, 0 0 14px rgba(255,255,255,.6)'
                  : c ? `0 0 10px ${c}88` : 'inset 0 0 8px rgba(0,0,0,.5)',
                transition: 'background .15s ease',
              }}
            />
          );
        })}
      </div>
      <SeatLegend />
    </div>
  );
}

function SeatLegend() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 18, fontSize: 12, color: 'rgba(234,255,234,.5)' }}>
      <Dot color={YOU_COLOR} label="YOU" />
      <Dot color={RIVAL_COLOR} label="RIVAL" />
    </div>
  );
}
function Dot({ color: c, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: c }} /> {label}
    </span>
  );
}
